import { useContext } from "react";
import axios from "axios";
import { GlobalContext } from "../../../../../contexts/GlobalContext";
import { keyUserId, keyAccessToken, keyUserActiveEnterpriseId } from "../../../../../assets/js/SessionStorageNames";

const useRcReport = () => {
    const globalContext = useContext(GlobalContext);

    const getHeaders = () => ({
        "x-api-key": process.env.REACT_APP_API_KEY,
        "x-access-token": localStorage.getItem(keyAccessToken),
        "x-user-id": localStorage.getItem(keyUserId),
    });

    const handleAxiosError = (err) => {
        let errorMessage = "Ocurrió un error al procesar la solicitud";
        if (err.response?.data?.message) {
            errorMessage = err.response.data.message;
        } else if (typeof err.response?.data === 'string') {
            errorMessage = err.response.data;
        } else if (err.response?.data) {
            errorMessage = JSON.stringify(err.response.data);
        } else if (err.message) {
            errorMessage = err.message;
        }
        return errorMessage;
    };

    // ─── Reportes de inventario y movimientos (sin cambios) ──────────────────

    const getInventoryReport = async (userName, userPassword, enterpriseInfo, login, reportType = 'report-sales-costs', getOwners = false, selectedOwner = null) => {
        let data;
        const ent_id = localStorage.getItem(keyUserActiveEnterpriseId);

        if (!login) {
            if (!userName || !userPassword || !enterpriseInfo || !ent_id) return;
            const enterprise = { id: enterpriseInfo.value, name: enterpriseInfo.label };
            data = { enterpriseId: ent_id, odooEnterpriceInfo: enterprise, odooUser: userName, odooPassword: userPassword, login: login };
            if (getOwners && reportType === 'report-movements') data.getOwners = getOwners;
            if (selectedOwner) data.selectedOwner = selectedOwner;
        } else {
            if (!userName || !userPassword || !ent_id) return;
            data = { enterpriseId: ent_id, odooUser: userName, odooPassword: userPassword, login: login };
        }

        try {
            const response = await axios({
                method: "post",
                url: `${process.env.REACT_APP_BACKEND_URL}/v1/odoo-report/${reportType}`,
                headers: getHeaders(),
                data: data,
            });
            if (login || getOwners) return response.data.data;
            globalContext.setLoaderPage(false);
            globalContext.showModalWithMsg(`La descarga ha iniciado correctamente, puede visualizar su estatus en la vista de segundo plano`);
        } catch (err) {
            globalContext.setLoaderPage(false);
            globalContext.showModalWithMsg(handleAxiosError(err));
        }
    };

    // ─── Reporte de lista de precios — endpoints atómicos ────────────────────

    /**
     * Busca marcas directamente en Odoo con filtro ilike y paginación.
     * Requiere credenciales de Odoo y companyId.
     */
    const searchOdooBrands = async (odooUser, odooPassword, odooCompanyId, search = '') => {
        const ent_id = localStorage.getItem(keyUserActiveEnterpriseId);
        if (!ent_id || !odooUser || !odooPassword || !odooCompanyId) return [];
        const response = await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/v1/odoo/search-brands`,
            { enterpriseId: ent_id, odooUser, odooPassword, odooCompanyId, search, limit: 50 },
            { headers: getHeaders() }
        );
        return response.data.data.brands || [];
    };

    /**
     * Busca categorías directamente en Odoo con filtro ilike y paginación.
     * Requiere credenciales de Odoo y companyId.
     */
    const searchOdooCategories = async (odooUser, odooPassword, odooCompanyId, search = '') => {
        const ent_id = localStorage.getItem(keyUserActiveEnterpriseId);
        if (!ent_id || !odooUser || !odooPassword || !odooCompanyId) return [];
        const response = await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/v1/odoo/search-categories`,
            { enterpriseId: ent_id, odooUser, odooPassword, odooCompanyId, search, limit: 50 },
            { headers: getHeaders() }
        );
        return response.data.data.categories || [];
    };

    /**
     * Autentica en Odoo y devuelve la lista de empresas disponibles.
     * Genérico — reutilizable por otros reportes.
     */
    const connectToOdoo = async (odooUser, odooPassword) => {
        const ent_id = localStorage.getItem(keyUserActiveEnterpriseId);
        if (!ent_id || !odooUser || !odooPassword) return null;
        const response = await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/v1/odoo/odoo-companies`,
            { enterpriseId: ent_id, odooUser, odooPassword },
            { headers: getHeaders() }
        );
        return response.data.data.companies || [];
    };

    /**
     * Obtiene las tarifas disponibles en Odoo para una empresa específica en tiempo real.
     * Genérico — reutilizable por otros reportes que necesiten tarifas Odoo.
     */
    const getOdooPricelists = async (odooUser, odooPassword, odooCompanyId) => {
        const ent_id = localStorage.getItem(keyUserActiveEnterpriseId);
        if (!ent_id || !odooUser || !odooPassword || !odooCompanyId) return [];
        const response = await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/v1/odoo-pricelist/${ent_id}/from-odoo`,
            { odooUser, odooPassword, odooCompanyId },
            { headers: getHeaders() }
        );
        return response.data.data.pricelists || [];
    };

    /**
     * Lanza la generación del reporte de lista de precios en segundo plano.
     * Devuelve el monitorId para hacer seguimiento del progreso.
     */
    /**
     * Lanza la generación del reporte de lista de precios en segundo plano.
     * odooWholesaleRateInfo: objeto completo de la tarifa al mayor (nombre, moneda, id)
     * odooRetailRateId: ID numérico de la tarifa PVP
     */
    const generatePricelistReport = async ({
        odooUser,
        odooPassword,
        odooEnterpriceInfo,
        odooWholesaleRateInfo,
        odooRetailRateId,
        banInventories,
        brandFilters,
        categoryFilters,
        onlyWithStock,
    }) => {
        const ent_id = localStorage.getItem(keyUserActiveEnterpriseId);
        if (!ent_id || !odooUser || !odooPassword || !odooEnterpriceInfo || !odooWholesaleRateInfo) return null;

        const data = {
            enterpriseId: ent_id,
            odooUser,
            odooPassword,
            odooEnterpriceInfo,
            odooWholesaleRateInfo,
            odooRetailRateId,
            banInventories: banInventories || [],
            onlyWithStock: onlyWithStock || false,
        };

        if (brandFilters?.values?.length > 0) data.brandFilters = brandFilters;
        if (categoryFilters?.values?.length > 0) data.categoryFilters = categoryFilters;

        const response = await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/v1/odoo-report/report-product-pricelist`,
            data,
            { headers: getHeaders() }
        );

        return response.data.data;
    };

    return {
        getInventoryReport,
        searchOdooBrands,
        searchOdooCategories,
        connectToOdoo,
        getOdooPricelists,
        generatePricelistReport,
        handleAxiosError,
    };
};

export default useRcReport;